import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import css from './reportModal.module.css';

const ReportModal = ({ isOpen, onClose, lesson }) => {
  const { t } = useTranslation();
  
  const [step, setStep] = useState(1); // 1: picker, 2: form, 3: success
  const [reportType, setReportType] = useState('');
  const [selCat, setSelCat] = useState(null);
  const [inputText, setInputText] = useState('');
  const [commentText, setCommentText] = useState('');

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setReportType('');
      setSelCat(null);
      setInputText('');
      setCommentText('');
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const openCtx = (type) => {
    setReportType(type);
    setSelCat(null);
    setStep(2);
  };

  const submitReport = () => {
    if (!selCat) return;
    const payload = {
      lessonId: lesson?.id,
      page: reportType,
      issueType: selCat,
      subject: inputText,
      comment: commentText,
      timestamp: new Date().toISOString()
    };
    console.log('📋 Report submitted (Mock):', payload);
    
    // Move to success step
    setStep(3);
    // Automatically close after a few seconds
    setTimeout(() => {
      handleClose();
    }, 3000);
  };

  if (!isOpen) return null;

  // Contexts use English keys for translation
  const CTX = {
    video: {
      label: 'Issue with the video',
      main: lesson?.video?.name || 'Video',
      meta: lesson?.name ? `${t('Lesson')}: ${lesson.name}` : '',
      cats: ['Video does not load', 'Video does not play', 'No sound', 'Subtitles are not displayed', 'Incorrect subtitles', 'Other']
    },
    word: {
      label: 'Issue with the word',
      main: '',
      meta: lesson?.name ? `${t('Lesson')}: ${lesson.name}` : '',
      cats: ['Incorrect translation', 'Translation is not displayed', 'Incorrect explanation', 'Incorrect example', 'Audio does not work', 'Other'],
      input: true,
      inputLabel: 'Which word?',
      inputPlaceholder: 'Type the word…'
    },
    quiz: {
      label: 'Issue with the question',
      main: '',
      meta: lesson?.name ? `Quiz · ${lesson.name}` : 'Quiz',
      cats: ['Incorrect question', 'Incorrect answer marked as correct', 'Incorrect answer options', 'Translation error', 'Question does not work', 'Other'],
      input: true,
      inputLabel: 'Question text',
      inputPlaceholder: 'Describe or type the question…'
    },
    speaking: {
      label: 'Issue with AI Speaking',
      main: '',
      meta: lesson?.name ? `Speaking · ${lesson.name}` : 'Speaking',
      cats: ['Voice recording does not work', 'Recording does not stop', 'Recording does not play', 'AI incorrectly recognized', 'AI incorrectly assessed', 'Incorrect hint', 'Other'],
      input: true,
      inputLabel: 'AI phrase',
      inputPlaceholder: 'Type the phrase…'
    },
    other: {
      label: 'Other issue',
      main: '',
      meta: lesson?.name ? `${t('Lesson')}: ${lesson.name}` : '',
      cats: ['Technical error', 'Page does not load', 'Something is displayed incorrectly', 'Other']
    }
  };

  const currentCtx = reportType ? CTX[reportType] : null;

  return (
    <div className={`${css.rptOv} ${css.show}`} onClick={(e) => { if(e.target === e.currentTarget) handleClose(); }}>
      {/* STEP 1: PICKER */}
      {step === 1 && (
        <div className={`${css.rptMd} ${css.rptPicker}`}>
          <div className={css.rptHead}>
            <div className={css.rptTop}>
              <div className={css.rptTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {t('Report an issue')}
              </div>
              <div className={css.rptX} onClick={handleClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </div>
            </div>
            <div className={css.rptSub}>{t('Choose what needs to be checked.')}</div>
          </div>
          <div className={css.rptBody} style={{ paddingBottom: '20px' }}>
            <div className={css.pkGrid}>
              <div className={css.pkCard} onClick={() => openCtx('video')}>
                <div className={`${css.pkIc} ${css.pkV}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('Issue with the video')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('word')}>
                <div className={`${css.pkIc} ${css.pkW}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('Issue with the word')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('quiz')}>
                <div className={`${css.pkIc} ${css.pkQ}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('Issue with the question')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('speaking')}>
                <div className={`${css.pkIc} ${css.pkS}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('Issue with AI Speaking')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
              <div className={css.pkCard} onClick={() => openCtx('other')}>
                <div className={`${css.pkIc} ${css.pkO}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg></div>
                <div className={css.pkBody}><div className={css.pkT}>{t('Other issue')}</div></div>
                <div className={css.pkArr}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: CONTEXTUAL FORM */}
      {step === 2 && currentCtx && (
        <div className={css.rptMd}>
          <div className={css.rptHead}>
            <div className={css.rptTop}>
              <div className={css.rptTitle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                {t('Report an issue')}
              </div>
              <div className={css.rptX} onClick={handleClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </div>
            </div>
            <div className={css.rptSub}>{t('Choose what needs to be checked.')}</div>
            <div className={css.rptCtx}>
              <div className={css.rptCtxLabel}>{t(currentCtx.label)}</div>
              {currentCtx.main && <div className={css.rptCtxMain}>{t(currentCtx.main)}</div>}
              <div className={css.rptCtxMeta}>{currentCtx.meta}</div>
            </div>
          </div>
          <div className={css.rptBody}>
            <div className={css.rptCl}>{t('What exactly is wrong?')}</div>
            <div className={css.rptCats}>
              {currentCtx.cats.map((cat, index) => (
                <div 
                  key={index} 
                  className={`${css.rptCat} ${selCat === cat ? css.sel : ''}`} 
                  onClick={() => setSelCat(cat)}
                >
                  <div className={css.rptCatR}></div>{t(cat)}
                </div>
              ))}
            </div>
            {currentCtx.input && (
              <div className={css.rptWinput}>
                <label>{t(currentCtx.inputLabel)}</label>
                <input 
                  placeholder={t(currentCtx.inputPlaceholder)} 
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
              </div>
            )}
            <div className={css.rptTa}>
              <label>{t('Comment — optional')}</label>
              <textarea 
                placeholder={t('Describe what needs to be fixed…')}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </div>
          </div>
          <div className={css.rptFoot}>
            <button className={`${css.rptBtn} ${css.rptBtnS}`} onClick={() => setStep(1)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
              {t('Back')}
            </button>
            <button 
              className={`${css.rptBtn} ${css.rptBtnP} ${!selCat ? css.dis : ''}`} 
              onClick={submitReport}
            >
              {t('Send')} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: SUCCESS */}
      {step === 3 && (
        <div className={css.rptMd}>
          <div className={`${css.rptSuc} ${css.show}`}>
            <div className={css.rptSucIc}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h3>{t('Thank you!')}</h3>
            <p>{t('We received your report and will investigate the issue.')}</p>
            <button className={`${css.rptBtn} ${css.rptBtnP}`} style={{ margin: '0 auto' }} onClick={handleClose}>
              {t('Close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportModal;